'use client';

import { useMemo } from 'react';
import { Copy, FileText, FileType, FileCode, LogIn } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Separator } from '@/components/ui/layout/separator';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { useToast } from '@/hooks/use-toast';

interface GuestResumeShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeName: string;
  /** The `/resumes/shared/:token` path once a server copy exists, else null. */
  shareUrl: string | null;
  token: string | null;
  onSignIn: () => void;
  /** Guest-mode export gate — shows the sign-in dialog (see `withGuestGate`
   *  in `ResumeBuilderClient`) instead of downloading a file. Exporting a
   *  PDF/Word/text copy requires an authenticated resume, so guests are
   *  routed through sign-in + migration rather than getting a free download. */
  onGuestExport: () => void;
}

const EXPORT_FORMATS: { value: 'pdf' | 'docx' | 'txt'; label: string; hint: string; icon: React.ReactNode }[] = [
  { value: 'pdf', label: 'PDF', hint: 'Sharing and printing', icon: <FileText className="h-4 w-4" /> },
  { value: 'docx', label: 'Word', hint: 'Editable in Word', icon: <FileType className="h-4 w-4" /> },
  { value: 'txt', label: 'Plain text', hint: 'ATS and copying', icon: <FileCode className="h-4 w-4" /> },
];

/**
 * Share & export for a guest (no-account) resume.
 *
 * Deliberately a separate, simpler component rather than a guest mode bolted
 * onto `ResumeShareExportDialog`: that dialog's share controls call the
 * authenticated `/api/resume-builder/[reportId]/share` enable/disable/
 * regenerate endpoint, which has no anonymous equivalent and structurally
 * can't (see the anonymous route's own doc comment) — a guest resume's one
 * link IS its only access path, so there is nothing to toggle. Keeping this
 * separate means the authenticated dialog's behavior for paying, signed-in
 * users is untouched by any of this.
 *
 * Export requires sign-in: clicking a format calls `onGuestExport` (wired to
 * `withGuestGate` in `ResumeBuilderClient`) rather than hitting
 * `/api/resumes/shared/[token]/download` directly. Sharing the link itself
 * stays free — only pulling a PDF/Word/text copy for yourself is gated.
 */
export function GuestResumeShareDialog({
  open,
  onOpenChange,
  resumeName,
  shareUrl,
  token,
  onSignIn,
  onGuestExport,
}: GuestResumeShareDialogProps) {
  const { toast } = useToast();

  const absoluteUrl = useMemo(() => {
    if (!shareUrl) return '';
    if (typeof window === 'undefined') return shareUrl;
    return `${window.location.origin}${shareUrl}`;
  }, [shareUrl]);

  const handleCopyLink = async () => {
    if (!absoluteUrl) return;
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      toast({ title: 'Link copied', description: 'Shared resume URL copied to clipboard.' });
    } catch {
      toast({ title: 'Failed to copy', description: 'Could not copy the link.', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeading
          title="Share &amp; export"
          description={<>Send a link to &quot;{resumeName}&quot;, or download a copy.</>}
        />

        <div className="space-y-4 py-2">
          {token ? (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Shared URL</label>
              <div className="flex items-center gap-2">
                <Input value={absoluteUrl} readOnly className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  disabled={!absoluteUrl}
                  aria-label="Copy shared link"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can view, download, and — unlike an authenticated share
                link — edit this resume, since it doubles as your own way back in. Sign in below
                for a resume that&apos;s private by default with sharing you control.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Preparing your share link…</p>
          )}

          <Separator />

          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium">Download a copy</p>
              <p className="text-xs text-muted-foreground">Sign in to download a PDF, Word, or plain-text copy.</p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {EXPORT_FORMATS.map((format) => (
                <Button
                  key={format.value}
                  type="button"
                  variant="outline"
                  onClick={onGuestExport}
                  className="h-auto flex-col items-start gap-0.5 px-3 py-2.5 text-left"
                >
                  <span className="flex w-full items-center gap-2">
                    {format.icon}
                    <span className="text-label">{format.label}</span>
                    <LogIn className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                  <span className="text-caption font-normal normal-case text-muted-foreground">
                    {format.hint}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-3 rounded-md border border-dashed p-3">
            <div>
              <p className="text-sm font-medium">Want more control?</p>
              <p className="text-xs text-muted-foreground">
                Sign in to save this to your account, manage sharing privately, and use AI tools.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onSignIn} className="shrink-0 gap-1.5">
              <LogIn className="h-4 w-4" />
              Sign in
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
